/* eslint-disable max-lines */
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { usePageHeader } from '@utils/hooks/use-page-header';
import Button from '@components/atoms/ui-kit/button.tsx';
import CreateUserForm from '@components/workspace/create-user-form.tsx';
import UpdateUserForm from '@components/workspace/update-user-form.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';

import DataGridV2, {
  DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';

import { useGetUsers } from '@utils/api/users/use-get-users.tsx';
import { useDeleteOrganizationUser } from '@utils/api/users/use-delete-organization-user.tsx';
import { useResendUserActivationLink } from '@utils/api/users/use-resend-activation-link-user.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { ensureTextOnBg } from '@utils/helpers/color-utils';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';

import { UserData } from '@utils/types/users/user.ts';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { Edit, Plus, Send, Trash2 } from 'lucide-react';
import { GridAction } from '@components/atoms/datatable/grid-action-controller.tsx';
import { PATHS } from '../../router/paths.ts';

type ValidationErr = { loc?: string[]; msg?: string };

function getInitials(first?: string, last?: string): string {
  const f = (first || '').trim();
  const l = (last || '').trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f) return f[0].toUpperCase();
  if (l) return l[0].toUpperCase();
  return '?';
}

export default function Users() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour, getBrandTint } = useThemeContext();

  const { data: users, isLoading, isFetching, error } = useGetUsers();
  const deleteUserMutation = useDeleteOrganizationUser();
  const resendActivationLink = useResendUserActivationLink();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);

  const [selectedUser, setSelectedUser] = useState<UserData | undefined>();
  const [isResending, setIsResending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (error && (error as AxiosError)?.response?.status === 403) {
      navigate(PATHS().HOME);
    }
  }, [error, navigate]);

  // Global search deep-links: `?edit=<id>` opens the update modal;
  // `?filter=<name>` narrows the visible rows client-side. We use `filter`
  // not `q` because `useGetUsers` forwards `?q=` to the server's
  // `get_search` dependency, which rejects plain strings with 400.
  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const q = searchParams.get('filter') ?? '';

  useEffect(() => {
    if (!editId || !users?.result) return;
    const match = users.result.find((u: UserData) => String(u.id) === editId);
    if (match) {
      setSelectedUser(match);
      setIsUpdateModalOpen(true);
    }
    // Scrub the param either way — if the id didn't resolve to a row (stale
    // link, user deleted), we don't want to keep re-triggering on refetch.
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      },
      { replace: true }
    );
  }, [editId, users?.result, setSearchParams]);

  const rows = useMemo<UserData[]>(() => {
    const all = users?.result ?? [];
    if (!q) return all;
    const needle = q.toLowerCase();
    return all.filter(
      (u: UserData) =>
        (u.first_name || '').toLowerCase().includes(needle) ||
        (u.last_name || '').toLowerCase().includes(needle) ||
        (u.email || '').toLowerCase().includes(needle)
    );
  }, [users, q]);

  const columns = useMemo<DataGridColumn<UserData>[]>(
    () => [
      {
        id: 'first_name',
        header: t('first-name'),
        field: 'first_name',
        minWidth: 280,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'first_name',
        serverOperator: 'ilike',
        enableRowCopyOnClick: false,
        cell: ({ row }) => {
          const u = row.original;
          const initials = getInitials(u.first_name, u.last_name);
          return (
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold leading-none"
                style={{
                  backgroundColor: getBrandTint(0.15),
                  color: ensureTextOnBg(
                    getColour('activeText'),
                    getColour('appBackground'),
                    5.5
                  ),
                  border: `1px solid ${getBrandTint(0.25)}`
                }}
                aria-hidden="true"
              >
                {initials}
              </div>
              <Text
                as="span"
                size="sm"
                className="truncate"
                title={u.first_name}
              >
                {u.first_name}
              </Text>
            </div>
          );
        }
      },
      {
        id: 'last_name',
        header: t('last-name'),
        field: 'last_name',
        minWidth: 200,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'last_name',
        serverOperator: 'ilike',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'email',
        header: t('email'),
        field: 'email',
        minWidth: 280,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'email',
        serverOperator: 'ilike',
        enableRowCopyOnClick: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'is_active',
        header: t('status'),
        field: 'is_active',
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'is_active',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('active'), value: 'true' },
          { label: t('inactive'), value: 'false' }
        ],
        cell: ({ getValue }) => {
          const active = Boolean(getValue());
          return (
            <Badge color={active ? 'green' : 'zinc'}>
              {active ? t('active') : t('inactive')}
            </Badge>
          );
        }
      },
      {
        id: 'roles',
        header: t('role'),
        field: 'roles',
        maxWidth: 240,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => {
          const roles = row.original.roles ?? [];
          if (roles.length === 0) {
            return (
              <Text as="span" size="xs" tone="muted">
                —
              </Text>
            );
          }
          return (
            <div className="flex flex-wrap items-center gap-1">
              {roles.map(r => {
                if (r === 'admin') {
                  return (
                    <span
                      key={r}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
                      style={{
                        backgroundColor: getBrandTint(0.12),
                        color: ensureTextOnBg(
                          getColour('activeText'),
                          getColour('appBackground'),
                          5.5
                        ),
                        boxShadow: `0 0 0 1px ${getBrandTint(0.25)}`
                      }}
                    >
                      {t(r)}
                    </span>
                  );
                }
                return (
                  <Badge key={r} color="purple">
                    {t(r)}
                  </Badge>
                );
              })}
            </div>
          );
        }
      }
    ],
    [t, getColour, getBrandTint]
  );

  const handleDeleteUser = () => {
    if (!selectedUser?.id) return;

    setIsDeleting(true);
    const toastId = toast.loading(t('deleting-user'));

    deleteUserMutation
      .mutateAsync(selectedUser.id)
      .then(() => {
        toast.success(t('user-deleted-successfully'), { id: toastId });
      })
      .catch((err: unknown) => {
        const fallbackMessage = t('an-error-has-occurred');
        const data = (err as AxiosError<BaseError>)?.response?.data;

        let messages: string[] = [];
        if (Array.isArray(data?.detail)) {
          messages = (data.detail as ValidationErr[]).map(e => {
            const field = e.loc?.[1] ?? 'field';
            return `${field}: ${e.msg}`;
          });
        } else if (data?.message) {
          messages = [data.message];
        } else if (data?.error_description) {
          messages = [data.error_description];
        } else {
          messages = [fallbackMessage];
        }
        toast.error(messages.join('\n'), { id: toastId });
      })
      .finally(() => {
        setIsDeleteModalOpen(false);
        setIsDeleting(false);
      });
  };

  const handleResendActivation = () => {
    if (!selectedUser?.id) return;

    const toastId = toast.loading(t('sending-reactivation-link'));
    setIsResending(true);

    resendActivationLink.mutate(
      { customerId: selectedUser.id },
      {
        onSuccess: () => {
          toast.success(t('reactivation-link-sent'), { id: toastId });
        },
        onError: (err: unknown) => {
          const fallbackMessage = t('failed-to-send-reactivation-link');
          const data = (err as AxiosError<BaseError>)?.response?.data;

          let messages: string[] = [];
          if (Array.isArray(data?.detail)) {
            messages = (data.detail as ValidationErr[]).map(e => {
              const field = e.loc?.[1] ?? 'field';
              return `${field}: ${e.msg}`;
            });
          } else if (data?.message) {
            messages = [data.message];
          } else if (data?.error_description) {
            messages = [data.error_description];
          } else {
            messages = [fallbackMessage];
          }
          toast.error(messages.join('\n'), { id: toastId });
        },
        onSettled: () => {
          setIsResending(false);
          setIsResendModalOpen(false);
        }
      }
    );
  };

  const actions = useMemo<GridAction<UserData>[]>(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <Edit className="h-4 w-4" />,
        intent: 'primary',
        run: row => {
          setSelectedUser(row);
          setIsUpdateModalOpen(true);
        }
      },
      {
        id: 'link',
        label: t('send-reactivation-link'),
        icon: <Send className="h-4 w-4" />,
        run: row => {
          setSelectedUser(row);
          setIsResendModalOpen(true);
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        run: row => {
          setSelectedUser(row);
          setIsDeleteModalOpen(true);
        }
      }
    ],
    [t]
  );

  const showEmptyState =
    rows.length === 0 && !isLoading && !isFetching && !!users;

  usePageHeader({
    title: t('users'),
    description: t(
      'list-of-all-the-users-including-their-name-contact-and-actions'
    ),
    actions: (
      <Button
        onClick={() => setIsCreateModalOpen(true)}
        icon={<Plus width={14} />}
        label={t('new-user')}
      />
    )
  });

  return (
    <div className="bg-app min-h-screen">
      <CreateUserForm
        isModalOpen={isCreateModalOpen}
        setIsModalOpen={setIsCreateModalOpen}
      />

      <div className="pb-4 sm:pb-0 relative">
        <DataGridV2<UserData>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          totalItems={users ? users.total_results : undefined}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          height="88vh"
          dense
          grid={false}
          striped
          loading={isLoading || isFetching || !users}
          getRowId={row => String(row.id)}
        />

        {/* Empty state CTA */}
        {showEmptyState && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
            <div className="pointer-events-auto flex flex-col items-center gap-3 mt-8">
              <Text as="p" size="sm" tone="muted">
                {t('no-users-yet')}
              </Text>
              <Button
                label={t('invite-first-user')}
                icon={<Plus width={14} />}
                onClick={() => setIsCreateModalOpen(true)}
                variant="primary"
                size="small"
              />
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteUser}
        size="small"
        title={t('delete-confirmation')}
        caption={t('are-you-sure-you-want-to-delete-this-user')}
        confirmLabel={isDeleting ? t('deleting') : t('delete')}
        cancelLabel={t('cancel')}
        confirmDisabled={isDeleting}
        destructive
        autoFocusConfirm
      />

      <ConfirmationModal
        isOpen={isResendModalOpen}
        onClose={() => setIsResendModalOpen(false)}
        onConfirm={handleResendActivation}
        size="small"
        title={t('resend-activation-link')}
        caption={t(
          'by-clicking-confirm-you-will-resend-activation-link-to-the-users-email-address-the-user-must-activate-their-account-to-access-the-application'
        )}
        confirmLabel={isResending ? t('resending') : t('resend')}
        cancelLabel={t('cancel')}
        confirmDisabled={isResending}
        destructive={false}
        autoFocusConfirm
      />

      {isUpdateModalOpen && selectedUser && (
        <UpdateUserForm
          updateUserModalOpen={isUpdateModalOpen}
          setUpdateUserModalOpen={setIsUpdateModalOpen}
          selectedUser={selectedUser}
        />
      )}
    </div>
  );
}
